import React, { useState, useEffect } from 'react';
import './IdTypeSelectionPage.css';

const IdTypeSelectionPage = ({ onContinue }) => {
  const [selected, setSelected] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  
  // Get verification options from URL parameters
  const getVerificationOptions = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const showPhysical = urlParams.get('physical') !== 'false'; // Default true
    const showDigital = urlParams.get('digital') !== 'false';   // Default true
    
    return { showPhysical, showDigital };
  };
  
  const { showPhysical, showDigital } = getVerificationOptions();
  
  useEffect(() => {
    // Auto-scroll to hide address bar on mobile browsers
    const hideAddressBar = () => {
      setTimeout(() => {
        window.scrollTo(0, 1);
      }, 100);
    };
    hideAddressBar();
    window.addEventListener('orientationchange', hideAddressBar);
    
    // If only one option is available, auto-select it
    if (showPhysical && !showDigital) {
      setSelected('id');
    } else if (!showPhysical && showDigital) {
      setSelected('digital');
    }
    
    return () => {
      window.removeEventListener('orientationchange', hideAddressBar);
    };
  }, [showPhysical, showDigital]);

  const handleContinue = () => {
    if (!selected) return;
    
    setIsInitializing(true);
    
    // Initialize hVerifySDK with selected flow type
    const initializeSDK = () => {
      if (window.hVerifySDK && !window.hVerifySDKInstance) {
        // Get device ID from URL token parameter
        const urlParams = new URLSearchParams(window.location.search);
        const deviceId = urlParams.get('token') || 'DEVICE001';
        
        // Map selected option to flow type
        const flowType = selected === 'id' ? 'physical' : 'digital';
        
        // Initialize SDK with WebSocket server
        const sdkInstance = new window.hVerifySDK('https://api-main.ageverification.at', {
          deviceId: deviceId,
          flow: flowType,
          autoConnect: true,
          autoStart: true,
          debug: true
        });

        // Make SDK available globally for other components
        window.hVerifySDKInstance = sdkInstance;
        
        // Store flow type globally for other components to access
        window.selectedFlow = flowType;
        
        console.log(`hVerifySDK initialized with flow: ${flowType}`);
        
        // Trigger callback setup in App.js
        if (window.setupSDKCallbacks) {
          window.setupSDKCallbacks();
        }
        
        // Proceed to next step
        setIsInitializing(false);
        onContinue();
      } else if (!window.hVerifySDK) {
        console.warn('hVerifySDK not available, retrying...');
        setTimeout(initializeSDK, 100);
      } else {
        // SDK already initialized, just proceed
        setIsInitializing(false);
        onContinue();
      }
    };
    
    // Initialize with a small delay to ensure script is loaded
    setTimeout(initializeSDK, 100);
  };

  return (
    <div className="container id-type-selection-container">
      <div>
        <div className="header">
          <div className="logo-placeholder">
            <img src={require('./logo.png')} alt="Logo" />
          </div>
          <div className="headline">Verifikation auswählen</div>
          <div className="logo-placeholder" style={{opacity:0}}></div>
        </div>
        <div className="subheadline">
          {!showPhysical && !showDigital 
            ? 'Keine Verifikationsoptionen verfügbar.' 
            : (showPhysical && showDigital 
              ? 'Zum Fortfahren wählen Sie eine Überprüfungsart:' 
              : 'Fortfahren mit der verfügbaren Überprüfungsart:')}
        </div>
        {(!showPhysical && !showDigital) ? (
          <div className="options">
            <div className="option-card" style={{opacity: 0.5, cursor: 'not-allowed'}}>
              <div className="option-texts">
                <div className="option-title">Keine Optionen verfügbar</div>
                <div className="option-desc">Kontaktieren Sie den Support</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="options">
          {showPhysical && (
            <div
              className={`option-card${selected === 'id' ? ' selected' : ''}`}
              data-option="id"
              onClick={() => setSelected('id')}
            >
            <div className="option-icon">
              <svg width="24" height="24" viewBox="0 0 24 24">
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: '#15335B', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#286ECC', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <rect width="24" height="24" rx="8" fill="#e8edf2"/>
                <path d="M19.5 5.25H16.9L15.62 3.33C15.48 3.12 15.25 3 15 3H9C8.75 3 8.52 3.12 8.38 3.33L7.1 5.25H4.5C3.26 5.25 2.25 6.26 2.25 7.5V18C2.25 19.24 3.26 20.25 4.5 20.25H19.5C20.74 20.25 21.75 19.24 21.75 18V7.5C21.75 6.26 20.74 5.25 19.5 5.25ZM12 16.5C9.72 16.5 7.88 14.65 7.88 12.38C7.88 10.1 9.72 8.25 12 8.25C14.28 8.25 16.12 10.1 16.12 12.38C16.12 14.65 14.28 16.5 12 16.5Z" fill="#15335B"/>
              </svg>
            </div>
            <div className="option-texts">
              <div className="option-title">Amtlicher Lichtbildausweis</div>
              <div className="option-desc">Verifizierung durch ID Scanner</div>
            </div>
          </div>
          )}
          {showDigital && (
            <div
              className={`option-card${selected === 'digital' ? ' selected' : ''}`}
              data-option="digital"
              onClick={() => setSelected('digital')}
            >
            <div className="option-icon">
              <svg width="24" height="24" viewBox="0 0 24 24">
                <rect width="24" height="24" rx="8" fill="#e8edf2"/>
                <path d="M16.5 1.5H7.5C6.26 1.5 5.25 2.51 5.25 3.75V20.25C5.25 21.49 6.26 22.5 7.5 22.5H16.5C17.74 22.5 18.75 21.49 18.75 20.25V3.75C18.75 2.51 17.74 1.5 16.5 1.5ZM6.75 6H17.25V18H6.75V6ZM7.5 3H16.5C16.91 3 17.25 3.34 17.25 3.75V4.5H6.75V3.75C6.75 3.34 7.09 3 7.5 3ZM16.5 21H7.5C7.09 21 6.75 20.66 6.75 20.25V19.5H17.25V20.25C17.25 20.66 16.91 21 16.5 21Z" fill="#0F141A"/>
              </svg>
            </div>
            <div className="option-texts">
              <div className="option-title">Digitale ID</div>
              <div className="option-desc">ID Austria oder eIDAS</div>
            </div>
          </div>
          )}
        </div>
        )}
      </div>
      <div className="bottom-section">
        <button
          className={`weiter-btn${selected ? ' active' : ''}`}
          disabled={!selected || isInitializing || (!showPhysical && !showDigital)}
          onClick={handleContinue}
        >
          {(!showPhysical && !showDigital) ? 'Nicht verfügbar' : 
           (isInitializing ? 'Initializing...' : 'Weiter')}
        </button>
      </div>
    </div>
  );
};

export default IdTypeSelectionPage;
